import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Users, Building2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { gov } from '../../lib/govApi';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';

const AGENCY_TYPES = [
  { v: 'state_agency', l: 'State Agency' },
  { v: 'county', l: 'County' },
  { v: 'city', l: 'City' },
  { v: 'school_district', l: 'School District' },
  { v: 'university', l: 'University' },
  { v: 'public_entity', l: 'Other Public Entity' },
];

const blankAgency = {
  name: '', type: 'state_agency', billing_address: '', shipping_address: '', tax_exempt: true, tax_exempt_id: '',
  contact_name: '', contact_email: '', contact_phone: '', po_required: true, approval_levels: 1, monthly_spending_limit: '',
};

const GovAgencyAdmin = () => {
  const { user } = useAuth();
  const isUske = user && (user.role === 'super_admin' || user.role === 'admin');
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(blankAgency);
  const [usersOpen, setUsersOpen] = useState(null); // agency object
  const [usersList, setUsersList] = useState([]);
  const [userForm, setUserForm] = useState({ name: '', email: '', role: 'purchaser', password: '', phone: '', department: '' });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await gov.agencies();
      setAgencies(r.data || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to load agencies', variant: 'destructive' });
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const submitAgency = async () => {
    try {
      const payload = { ...form, monthly_spending_limit: form.monthly_spending_limit ? Number(form.monthly_spending_limit) : null };
      await gov.createAgency(payload);
      toast({ title: 'Agency created' });
      setCreateOpen(false);
      setForm(blankAgency);
      fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const deleteAgency = async (id, name) => {
    if (!window.confirm(`Delete agency "${name}"?`)) return;
    try {
      await gov.deleteAgency(id);
      toast({ title: 'Agency deleted' });
      fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const openUsers = async (agency) => {
    setUsersOpen(agency);
    try {
      const r = await gov.listAgencyUsers(agency.id);
      setUsersList(r.data || []);
    } catch (e) { setUsersList([]); }
  };

  const addUser = async () => {
    try {
      await gov.createAgencyUser(usersOpen.id, userForm);
      toast({ title: 'User created' });
      setUserForm({ name: '', email: '', role: 'purchaser', password: '', phone: '', department: '' });
      const r = await gov.listAgencyUsers(usersOpen.id);
      setUsersList(r.data || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-5" data-testid="gov-agency-admin-page">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2"><Building2 className="h-6 w-6 text-blue-700" /> Agencies</h1>
          <p className="text-sm text-slate-500">Manage authorized purchasers across Washington State.</p>
        </div>
        {isUske && <Button className="bg-blue-700 hover:bg-blue-800 text-white" onClick={() => setCreateOpen(true)} data-testid="gov-agency-add-btn"><Plus className="h-4 w-4 mr-1" />New agency</Button>}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {agencies.map(a => (
            <Card key={a.id} className="border-2" data-testid={`gov-agency-card-${a.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold text-slate-800">{a.name}</div>
                    <div className="text-xs text-slate-500 capitalize">{(a.type || '').replace(/_/g, ' ')}</div>
                  </div>
                  {isUske && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteAgency(a.id, a.name)}><Trash2 className="h-4 w-4" /></Button>}
                </div>
                <div className="text-xs text-slate-600 mt-2 space-y-1">
                  <div><b>PO required:</b> {a.po_required ? 'Yes' : 'No'}</div>
                  <div><b>Approval levels:</b> {a.approval_levels}</div>
                  <div><b>Monthly limit:</b> {a.monthly_spending_limit ? `$${Number(a.monthly_spending_limit).toLocaleString()}` : '—'}</div>
                  <div><b>Contact:</b> {a.contact_email || '—'}</div>
                </div>
                <div className="mt-3"><Button size="sm" variant="outline" onClick={() => openUsers(a)} data-testid={`gov-agency-users-${a.id}`}><Users className="h-4 w-4 mr-1" />Users</Button></div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New Agency</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Agency name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agency-name" /></div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{AGENCY_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Tax exempt ID</Label><Input value={form.tax_exempt_id} onChange={(e) => setForm({ ...form, tax_exempt_id: e.target.value })} /></div>
            <div className="col-span-2"><Label>Billing address</Label><Input value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} /></div>
            <div className="col-span-2"><Label>Shipping address</Label><Input value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} /></div>
            <div><Label>Contact name</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div><Label>Contact email</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
            <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
            <div>
              <Label>Approval levels</Label>
              <Select value={String(form.approval_levels)} onValueChange={(v) => setForm({ ...form, approval_levels: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1">1 (single)</SelectItem><SelectItem value="2">2 (supervisor + finance)</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Monthly spending limit ($)</Label><Input type="number" value={form.monthly_spending_limit} onChange={(e) => setForm({ ...form, monthly_spending_limit: e.target.value })} /></div>
            <div className="flex items-center gap-2 col-span-2">
              <input id="po_req" type="checkbox" checked={form.po_required} onChange={(e) => setForm({ ...form, po_required: e.target.checked })} />
              <label htmlFor="po_req" className="text-sm">Purchase Order # required</label>
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-3">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button className="bg-blue-700 hover:bg-blue-800 text-white" onClick={submitAgency} data-testid="agency-save">Create</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Users dialog */}
      <Dialog open={!!usersOpen} onOpenChange={(o) => { if (!o) { setUsersOpen(null); setUsersList([]); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Users — {usersOpen?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="border rounded">
              <table className="w-full text-sm">
                <thead className="bg-slate-50"><tr><th className="text-left p-2">Name</th><th className="text-left p-2">Email</th><th className="text-left p-2">Role</th></tr></thead>
                <tbody>
                  {usersList.length === 0 && <tr><td colSpan={3} className="p-3 text-center text-slate-500">No users yet.</td></tr>}
                  {usersList.map(u => (
                    <tr key={u.id} className="border-t" data-testid={`agency-user-${u.id}`}>
                      <td className="p-2">{u.name}</td>
                      <td className="p-2 text-slate-600">{u.email}</td>
                      <td className="p-2 capitalize">{(u.role || '').replace(/_/g, ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-slate-50 p-3 rounded space-y-2">
              <div className="font-semibold text-sm">Add user</div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Name *" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} data-testid="new-user-name" />
                <Input placeholder="Email *" type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} data-testid="new-user-email" />
                <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="purchaser">Purchaser</SelectItem>
                    <SelectItem value="approver">Approver</SelectItem>
                    <SelectItem value="finance_officer">Finance Officer</SelectItem>
                    <SelectItem value="agency_admin">Agency Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Initial password *" type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} data-testid="new-user-password" />
              </div>
              <Button onClick={addUser} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="add-user-btn">Create user</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GovAgencyAdmin;
